/**
 * THE PAGE INDEX: one line per stored page render of a reference PDF, riding the kit's profile.
 *
 * MOVED HERE FROM `deck15/kit/kitIntakeOps.ts` (2026-09-05), where the intake pipeline still lives and
 * re-exports every name below, so no caller's import path changed. The reason for the move is one
 * import direction: `bindingProfile` in `birtvy/brandKitDefault.ts` has to strip the index off a profile
 * before it rides a deck blob, and design-system must not import deck15. The helpers are pure and know
 * nothing about intake, so they belong beside `kitTraces.ts` and `kitSite.ts`, the other two "this key
 * is the live kit's business" stores.
 *
 * Pure: profile in, values out. No fetch, no DOM.
 */
import type { MoodboardProfile } from '../types/presentation'

/**
 * ONE PAGE OF A REFERENCE PDF: a URL into the kit's asset store, plus what the page is for.
 *
 * The renders used to be made and dropped — twelve went to the vision pass, six were shrunk to 400px
 * thumbnails, and the rest were collected the moment `readPdfIntoKit` returned. That was right while a
 * PDF was only a source of colours and fonts. It is wrong now that a PDF is a set of LAYOUTS: nobody can
 * write a composition against page 30 of a brand template if page 30 no longer exists.
 */
export interface KitPageEntry {
  /**
   * The page's own number in the source document, 1-based, so the sheet can label a tile with a number
   * the user can turn to in the PDF itself. Past `RASTER_PAGE_CAP` the render run samples evenly, so
   * these are the pages it PICKED and the run is no longer consecutive; `KitPageIndex.atCap` records
   * that, because a sheet showing 1, 3, 6, 9 should say it is a sample rather than look like a gap.
   *
   * `numberPages` fills this positionally for a renderer that answers with bare data URLs, which is the
   * only case where it is an ordinal rather than a page number.
   */
  page: number
  /** Where the render lives. Never the render itself — see the byte arithmetic on `uploadPageRender`. */
  assetUrl: string
  /** What a person called this page on the sheet. Free text; nothing branches on it. */
  label?: string
  /** The role slot this page answers, once somebody drops it on one. One page per role. */
  role?: string
  /** Furniture: a cover, a blank, a legal page. Kept in the index so dropping it survives a reload. */
  dropped?: boolean
}

/** The pages of one reference document, in the order they were rendered. */
export interface KitPageIndex {
  /** The file they came out of, so the sheet can name what it is showing. */
  fileName: string
  at: number
  pages: KitPageEntry[]
  /**
   * The run hit `RASTER_PAGE_CAP`, so this is an even sample of a longer document rather than all of it.
   * The `page` numbers stay true; what stops being true is that they are consecutive.
   */
  atCap?: boolean
}

export type ProfileWithPages = MoodboardProfile & { pdfPages?: KitPageIndex }

/**
 * How many pages we RASTERISE.
 *
 * WAS 24, WHICH MEANT PAGE 25 OF A 116-PAGE BRAND TEMPLATE DID NOT EXIST. That was the right number
 * while a PDF was a source of colours: 24 renders spread over a long deck read its palette as well as
 * 200 would, and the vision pass never saw more than a dozen of them anyway. It is the wrong number the
 * moment the pages ARE the deliverable, because the 92 that were never rendered are most of the layouts
 * the file was dropped here for.
 *
 * 160, and the arithmetic behind it: the largest real template we have is 116 pages, and 160 leaves a
 * third over it for the next one. The cost is linear and all of it is the client's. pdf.js draws each
 * page onto a canvas the size of a slide and JPEG-encodes it, on the order of a fifth of a second each,
 * so 116 pages is one to two minutes with the tab busy. Each render is around 400 KB as a base64 JPEG,
 * so the array alone is roughly 60 MB at the cap and the same volume goes up to the asset store once.
 * Past 160 the render stops being a wait and the memory starts being a swap.
 *
 * A longer document is sampled evenly by `pdfToPageImages` rather than truncated, and
 * `KitPageIndex.atCap` records that it happened, so the sheet can say it is showing a sample of a longer
 * file rather than leave the gaps in its page numbers looking like renders that failed.
 */
export const RASTER_PAGE_CAP = 160

const pageNum = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : undefined
const text = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

/**
 * Defensive read, exactly as `readSiteCapture` is: this arrives as JSON off a column written by more
 * than one version of this code, and a malformed record answers null rather than crashing the kit page.
 * An entry with no URL is an upload that failed and is dropped on the way in.
 */
export function readPageIndex(profile: MoodboardProfile | null | undefined): KitPageIndex | null {
  const raw = (profile as ProfileWithPages | null | undefined)?.pdfPages
  if (!raw || typeof raw !== 'object') return null
  const pages: KitPageEntry[] = []
  for (const p of Array.isArray(raw.pages) ? raw.pages : []) {
    const entry = p as Partial<KitPageEntry> | null
    const assetUrl = text(entry?.assetUrl)
    if (!assetUrl || pages.length >= RASTER_PAGE_CAP) continue
    const label = text(entry?.label)
    const role = text(entry?.role)
    pages.push({
      page: pageNum(entry?.page) ?? pages.length + 1,
      assetUrl,
      ...(label ? { label } : {}),
      ...(role ? { role } : {}),
      ...(entry?.dropped === true ? { dropped: true } : {}),
    })
  }
  if (pages.length === 0) return null
  return {
    fileName: text(raw.fileName) ?? 'the reference',
    at: typeof raw.at === 'number' && Number.isFinite(raw.at) ? raw.at : 0,
    pages,
    ...(raw.atCap === true ? { atCap: true } : {}),
  }
}

/**
 * Write the index onto a profile, or clear it.
 *
 * REPLACES, never appends, and that is a v1 decision with a cost worth stating: the previous document's
 * renders stay in the asset store with nothing pointing at them. Appending is worse until a template
 * record owns each set — one sheet holding two documents' pages, with no template id to tell them
 * apart, is a sheet nobody can reason about. That is Stage 5's `template_id`, not this.
 */
export function withPageIndex(profile: MoodboardProfile, index: KitPageIndex | null): MoodboardProfile {
  const rest: ProfileWithPages = { ...(profile as ProfileWithPages) }
  delete rest.pdfPages
  return index ? ({ ...rest, pdfPages: index } as MoodboardProfile) : (rest as MoodboardProfile)
}

/** A deck binding snapshots the kit; the pages are the live kit's business, like the references. */
export function stripPageIndex(profile: MoodboardProfile): MoodboardProfile {
  return withPageIndex(profile, null)
}

/** The pages the sheet still offers: everything nobody threw out. */
export function keptPages(index: KitPageIndex | null | undefined): KitPageEntry[] {
  return index ? index.pages.filter((p) => !p.dropped) : []
}

/** The page a role was given, if any. What a generation against this template reaches for. */
export function pageForRole(index: KitPageIndex | null | undefined, role: string): KitPageEntry | null {
  return keptPages(index).find((p) => p.role === role) ?? null
}

/**
 * Put `role` on `page`, or clear that page's role with null.
 *
 * ONE PAGE PER ROLE, enforced here rather than in the sheet: the role comes off whatever page held it
 * before. A role holding two pages is two reference images for one slot and no rule for choosing
 * between them, so the writer would get whichever the array happened to yield first.
 */
export function setPageRole(index: KitPageIndex, page: number, role: string | null): KitPageIndex {
  return {
    ...index,
    pages: index.pages.map((p) => {
      const { role: had, ...rest } = p
      if (p.page === page) return role ? { ...rest, role } : rest
      return role && had === role ? rest : p
    }),
  }
}

/**
 * Drop a page out of the set, or put it back.
 *
 * Dropping clears the role too. A page that is furniture cannot also be the answer to "statement", and
 * a role left on a dropped page makes `pageForRole` and the sheet disagree about whether a slot is full.
 *
 * Un-dropping a page that was never dropped is the identity, deliberately: it must not be a back door
 * that strips the role off a page somebody had just assigned.
 */
export function setPageDropped(index: KitPageIndex, page: number, dropped: boolean): KitPageIndex {
  return {
    ...index,
    pages: index.pages.map((p) => {
      if (p.page !== page || (!dropped && !p.dropped)) return p
      // Rebuilt field by field rather than spread-minus-key, because both directions land on the same
      // entry: dropping takes the role off, and un-dropping has nothing to give back since the drop
      // already took it.
      const bare: KitPageEntry = { page: p.page, assetUrl: p.assetUrl, ...(p.label ? { label: p.label } : {}) }
      return dropped ? { ...bare, dropped: true } : bare
    }),
  }
}
